import { Flex, Text } from '@radix-ui/themes'
import type { TooltipProps as RechartsTooltipProps } from 'recharts'

type ChartTooltipEntry = {
  name: string
  value: number
  color: string
}

type ChartTooltipProps = RechartsTooltipProps<number, string> & {
  valueFormatter?: (value: number) => string
}

const formatCount = (value: number): string => value.toLocaleString('en-GB')

export const ChartTooltip = ({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter = formatCount,
}: ChartTooltipProps) => {
  if (!active || !payload?.length) return null

  const formattedLabel = labelFormatter ? labelFormatter(label, payload) : label

  const entries: ChartTooltipEntry[] = payload
    .filter((p) => p.type !== 'none' && p.value != null && !Array.isArray(p.value))
    .map((p) => ({
      name: (p.name ?? p.dataKey ?? '') as string,
      value: p.value as number,
      color: (p.color ?? p.stroke ?? 'var(--gray-9)') as string,
    }))

  return (
    <div className="rounded-lg border border-[var(--gray-4)] bg-[var(--color-background)] px-3 py-2 shadow-md">
      <Text size="1" weight="medium" className="text-[var(--gray-10)] mb-1.5 block">
        {formattedLabel}
      </Text>
      <Flex direction="column" gap="1">
        {entries.map((entry) => (
          <Flex key={entry.name} align="center" justify="between" gap="4">
            <Flex align="center" gap="2">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <Text size="1" className="text-[var(--gray-11)]">
                {entry.name}
              </Text>
            </Flex>
            <Text size="1" weight="medium" className="text-[var(--gray-12)] tabular-nums">
              {valueFormatter(entry.value)}
            </Text>
          </Flex>
        ))}
      </Flex>
    </div>
  )
}
